import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Copy, Check, ShieldCheck, Users as UsersIcon, UserCircle2, KeyRound, MessageCircle, Phone, ClipboardList } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { USERS_T } from "@/lib/usersI18n";
import { fmtDateTime } from "@/lib/format";

/**
 * STAS Control Center → Users & Access
 * - Lists all users as Smart Cards (mobile-first).
 * - STAS can: create, edit (username/full_name/mobile/email/role/active),
 *   change password (new + confirm only), switch-into, delete.
 * - All actions audit-logged on backend.
 */
export default function UsersManagement() {
  const { user, refreshMe } = useAuth();
  const { theme, lang, dir, t } = useI18n();
  const navigate = useNavigate();
  const T = USERS_T[lang];

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  // Three organised buckets: admin (STAS + managers + accountant),
  // employees (internal but non-management), clients.
  const [filter, setFilter] = useState("admin"); // admin | employees | clients
  const [search, setSearch] = useState("");

  const ADMIN_ROLE_CODES = ["general_manager", "deputy_manager", "accountant"];
  const bucketOf = (u) => {
    if (u.user_type === "client") return "clients";
    if (u.role?.is_root || ADMIN_ROLE_CODES.includes(u.role?.code)) return "admin";
    return "employees";
  };

  const [modal, setModal] = useState(null); // { kind, user? }

  const isRoot = !!user?.role?.is_root;

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [u, r] = await Promise.all([
        api.get("/users"),
        api.get("/roles"),
      ]);
      setUsers(u.data || []);
      setRoles(r.data || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { admin: 0, employees: 0, clients: 0 };
    users.forEach((u) => { c[bucketOf(u)] += 1; });
    return c;
  }, [users]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return users.filter((u) => {
      if (bucketOf(u) !== filter) return false;
      if (!s) return true;
      return (
        (u.full_name || "").toLowerCase().includes(s) ||
        (u.username || "").toLowerCase().includes(s) ||
        (u.mobile_number || "").toLowerCase().includes(s) ||
        (u.email || "").toLowerCase().includes(s)
      );
    });
  }, [users, filter, search]);

  if (!isRoot) {
    return (
      <div dir={dir} style={{ padding: "40px", textAlign: "center", color: theme.errorText }}>
        {T.not_root}
      </div>
    );
  }

  const styles = makeStyles(theme);

  return (
    <div dir={dir} style={styles.page} data-testid="users-mgmt-page">
      <header style={styles.header}>
        <button onClick={() => navigate("/")} style={styles.backBtn} data-testid="users-back-button">
          {dir === "rtl" ? "→" : "←"} {T.back}
        </button>
        <div style={styles.titleWrap}>
          <KeyRound size={20} color={theme.gold} strokeWidth={2.2} />
          <div style={styles.title}>{T.title}</div>
        </div>
        <button
          onClick={() => setModal({ kind: "create", defaultType: filter === "clients" ? "client" : "internal" })}
          style={styles.primaryBtn}
          data-testid="users-create-button"
        >
          + {T.create}
        </button>
      </header>

      {/* Three organised buckets — Admin / Employees / Clients */}
      <div style={styles.tabRow} role="tablist">
        {[
          { k: "admin",     label: T.tab_admin,     hint: T.tab_admin_hint,     Icon: ShieldCheck,  color: "#d4af37" },
          { k: "employees", label: T.tab_employees, hint: T.tab_employees_hint, Icon: UsersIcon,    color: "#3b82f6" },
          { k: "clients",   label: T.tab_clients,   hint: T.tab_clients_hint,   Icon: UserCircle2,  color: "#16a34a" },
        ].map(({ k, label, hint, Icon, color }) => {
          const active = filter === k;
          return (
            <button
              key={k}
              onClick={() => setFilter(k)}
              style={styles.tabBtn(active, color)}
              data-testid={`users-tab-${k}`}
              role="tab"
              aria-selected={active}
            >
              <span style={styles.tabIconWrap(active, color)}>
                <Icon size={20} strokeWidth={2.2} color={active ? "#fff" : color} />
              </span>
              <span style={styles.tabTexts}>
                <span style={styles.tabLabel}>{label}</span>
                <span style={styles.tabHint}>{hint}</span>
              </span>
              <span style={styles.tabCount(active, color)}>{counts[k] ?? 0}</span>
            </button>
          );
        })}
      </div>

      <div style={styles.toolbar}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={T.search_placeholder}
          style={styles.search}
          data-testid="users-search-input"
        />
        <div style={styles.currentBucket}>
          {T.count_users(filtered.length)}
        </div>
      </div>

      {filter === "clients" && (
        <div
          data-testid="clients-marketing-hint"
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 14px", marginBottom: 12,
            borderRadius: 12,
            background: "rgba(22,163,74,0.08)",
            border: `1px solid rgba(22,163,74,0.24)`,
            color: theme.textSoft, fontSize: 12.5, lineHeight: 1.55,
          }}
        >
          <ShieldCheck size={16} strokeWidth={2} color="#16a34a" />
          <span>{T.marketing_only_hint}</span>
        </div>
      )}

      {err && <div style={styles.error} data-testid="users-error">{err}</div>}
      {loading ? (
        <div style={styles.loading} data-testid="users-loading">{t("app.checking_session")}</div>
      ) : filtered.length === 0 ? (
        <div style={styles.empty} data-testid="users-empty">{T.empty}</div>
      ) : (
        <div style={styles.grid}>
          {filtered.map((u) => (
            u.user_type === "client" ? (
              <ClientContactCard
                key={u.id}
                u={u}
                theme={theme}
                lang={lang}
                dir={dir}
                T={T}
                onAction={(kind) => {
                  if (kind === "view_apps") {
                    navigate(`/applications?search=${encodeURIComponent(u.mobile_number || u.full_name)}`);
                    return;
                  }
                  setModal({ kind, user: u });
                }}
              />
            ) : (
              <UserCard
                key={u.id}
                u={u}
                theme={theme}
                lang={lang}
                T={T}
                isSelf={u.id === user?.id}
                onAction={(kind) => {
                  if (kind === "features") {
                    navigate(`/admin/users/${u.id}/features`);
                    return;
                  }
                  setModal({ kind, user: u });
                }}
              />
            )
          ))}
        </div>
      )}

      {modal && (
        <UserModal
          modal={modal}
          roles={roles}
          theme={theme}
          lang={lang}
          T={T}
          onClose={() => setModal(null)}
          onDone={async (refetchSelf) => {
            setModal(null);
            await load();
            if (refetchSelf) await refreshMe();
          }}
        />
      )}
    </div>
  );
}

/* ────────────────────────── Smart Card ────────────────────────── */
function UserCard({ u, theme, lang, T, isSelf, onAction }) {
  const roleName = u.role ? (lang === "en" ? u.role.name_en || u.role.name_ar : u.role.name_ar) : "—";
  const isRoot = !!u.role?.is_root;
  const lastLogin = u.last_login_at
    ? fmtDateTime(u.last_login_at)
    : T.never_logged_in;

  const css = {
    card: {
      background: theme.surfaceCard,
      border: `1px solid ${isRoot ? theme.gold : theme.borderSoft}`,
      borderRadius: "14px",
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      position: "relative",
      boxShadow: isRoot ? `0 0 0 1px ${theme.borderAccent}` : "none",
    },
    head: { display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "flex-start" },
    name: { fontWeight: 800, fontSize: "15px", color: theme.text },
    sub: { fontSize: "12px", color: theme.textMuted, marginTop: "2px" },
    badges: { display: "flex", gap: "6px", flexWrap: "wrap" },
    badge: (color, bg) => ({
      fontSize: "10px",
      padding: "3px 8px",
      borderRadius: "12px",
      background: bg,
      color,
      fontWeight: 700,
      letterSpacing: "0.3px",
    }),
    meta: { fontSize: "12px", color: theme.textSoft, lineHeight: 1.7 },
    metaRow: { display: "flex", justifyContent: "space-between", gap: "8px" },
    metaLabel: { color: theme.textMuted },
    actions: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
      gap: "6px",
      marginTop: "8px",
      paddingTop: "10px",
      borderTop: `1px solid ${theme.borderSoft}`,
    },
    btn: (variant) => {
      const base = {
        padding: "8px 10px",
        borderRadius: "8px",
        fontWeight: 700,
        fontSize: "12px",
        cursor: "pointer",
        border: "1px solid transparent",
      };
      if (variant === "primary")
        return { ...base, background: theme.goldGradient, color: theme.name === "dark" ? "#0a0a0a" : "#fff" };
      if (variant === "danger")
        return { ...base, background: "transparent", color: theme.errorText, borderColor: theme.errorBorder };
      return { ...base, background: "transparent", color: theme.text, borderColor: theme.border };
    },
  };

  return (
    <div style={css.card} data-testid={`user-card-${u.username || u.id}`}>
      <div style={css.head}>
        <div>
          <div style={css.name}>{u.full_name}</div>
          <div style={css.sub}>
            {u.username ? `@${u.username}` : (u.mobile_number || "—")}
          </div>
        </div>
        <div style={css.badges}>
          {isRoot && (
            <span style={css.badge("#0a0a0a", theme.gold)}>ROOT</span>
          )}
          <span
            style={css.badge(
              u.is_active ? "#16a34a" : "#dc2626",
              u.is_active ? "rgba(34,197,94,0.12)" : "rgba(220,38,38,0.12)"
            )}
            data-testid={`user-status-${u.username || u.id}`}
          >
            {u.is_active ? T.active : T.inactive}
          </span>
          <span style={css.badge(theme.textSoft, theme.name === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)")}>
            {u.user_type === "internal" ? T.internal : T.client}
          </span>
        </div>
      </div>

      <div style={css.meta}>
        <div style={css.metaRow}>
          <span style={css.metaLabel}>{T.role}</span>
          <span>{roleName}</span>
        </div>
        <div style={css.metaRow}>
          <span style={css.metaLabel}>{T.last_login}</span>
          <span style={{ fontSize: "11px" }}>{lastLogin}</span>
        </div>
        {u.email && (
          <div style={css.metaRow}>
            <span style={css.metaLabel}>{T.email}</span>
            <span style={{ direction: "ltr", fontSize: "11px" }}>{u.email}</span>
          </div>
        )}
        {u.mobile_number && u.username && (
          <div style={css.metaRow}>
            <span style={css.metaLabel}>{T.mobile}</span>
            <span style={{ direction: "ltr", fontSize: "11px" }}>{u.mobile_number}</span>
          </div>
        )}
      </div>

      <div style={css.actions}>
        <button
          onClick={() => onAction("edit")}
          style={css.btn("primary")}
          data-testid={`user-edit-${u.username || u.id}`}
        >
          {T.edit}
        </button>
        <button
          onClick={() => onAction("password")}
          style={css.btn()}
          disabled={isRoot && !isSelf}
          data-testid={`user-password-${u.username || u.id}`}
        >
          {T.password}
        </button>
        <button
          onClick={() => onAction("toggle")}
          style={css.btn()}
          disabled={isRoot}
          data-testid={`user-toggle-${u.username || u.id}`}
          hidden={isRoot}
        >
          {u.is_active ? T.deactivate : T.activate}
        </button>
        {u.user_type === "internal" && !isSelf && (
          <button
            onClick={() => onAction("switch")}
            style={css.btn()}
            disabled={!u.is_active}
            data-testid={`user-switch-${u.username || u.id}`}
          >
            {T.switch_to}
          </button>
        )}
        {u.user_type === "internal" && !isSelf && (
          <button
            onClick={() => onAction("features")}
            style={css.btn()}
            disabled={isRoot}
            data-testid={`user-features-${u.username || u.id}`}
          >
            إدارة الأيقونات
          </button>
        )}
        {!isRoot && !isSelf && (
          <button
            onClick={() => onAction("delete")}
            style={css.btn("danger")}
            data-testid={`user-delete-${u.username || u.id}`}
          >
            {T.delete}
          </button>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────── Client Contact Card (marketing only) ──────────────────────────
 * Clients have NO login credentials — this card intentionally hides:
 *   • password reset  • toggle active  • switch user  • features
 * It exposes marketing/operations affordances instead:
 *   • edit contact info  • WhatsApp / Call  • view linked applications  • delete
 */
function ClientContactCard({ u, theme, lang, dir, T, onAction }) {
  const mobile = (u.mobile_number || "").replace(/[^\d+]/g, "");
  const whatsappHref = mobile ? `https://wa.me/${mobile.replace(/^\+/, "")}` : null;
  const callHref = mobile ? `tel:${mobile}` : null;
  const initial = (u.full_name || "?").trim().charAt(0).toUpperCase();
  const palette = ["#16A34A", "#3B82F6", "#D4AF37", "#8B5CF6", "#EC4899", "#14B8A6"];
  const hash = Array.from(u.full_name || "?").reduce((s, c) => s + c.charCodeAt(0), 0);
  const avatarColor = palette[hash % palette.length];

  const css = {
    card: {
      background: theme.surfaceCard,
      border: `1px solid ${theme.borderSoft}`,
      borderRadius: 16,
      padding: 16,
      display: "flex", flexDirection: "column", gap: 12,
      position: "relative",
      boxShadow: theme.cardShadow,
      transition: "border-color 160ms cubic-bezier(.2,.8,.2,1), transform 160ms cubic-bezier(.2,.8,.2,1)",
    },
    top: { display: "flex", alignItems: "center", gap: 12 },
    avatar: {
      width: 44, height: 44, borderRadius: 13,
      background: `linear-gradient(135deg, ${avatarColor}CC, ${avatarColor}88)`,
      color: "#fff", fontWeight: 900, fontSize: 16,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, boxShadow: `0 4px 12px ${avatarColor}33`,
    },
    nameCol: { flex: 1, minWidth: 0 },
    name: {
      fontSize: 15, fontWeight: 800, color: theme.text, letterSpacing: "-0.1px",
      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    },
    mobile: {
      fontSize: 12, color: theme.textMuted, marginTop: 2, direction: "ltr",
      fontFamily: "SF Mono, ui-monospace, monospace", textAlign: dir === "rtl" ? "right" : "left",
    },
    marketingBadge: {
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 9px", borderRadius: 999,
      background: "rgba(22,163,74,0.12)",
      border: "1px solid rgba(22,163,74,0.28)",
      color: "#16a34a", fontSize: 10, fontWeight: 800, letterSpacing: 0.3,
      flexShrink: 0,
    },
    email: {
      fontSize: 12, color: theme.textSoft, direction: "ltr",
      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      display: "flex", alignItems: "center", gap: 6,
    },
    quickRow: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 6,
    },
    quickBtn: (color, bg, border) => ({
      padding: "8px 10px",
      borderRadius: 10,
      background: bg,
      border: `1px solid ${border}`,
      color,
      fontWeight: 700, fontSize: 11.5, cursor: "pointer",
      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5,
      fontFamily: "inherit",
      textDecoration: "none",
      transition: "background 160ms",
    }),
    footer: {
      display: "flex", gap: 6, marginTop: 4,
      paddingTop: 10, borderTop: `1px solid ${theme.borderSoft}`,
    },
    ghostBtn: {
      flex: 1,
      padding: "8px 10px",
      borderRadius: 10,
      background: "transparent",
      color: theme.text,
      border: `1px solid ${theme.border}`,
      fontWeight: 700, fontSize: 12, cursor: "pointer",
      fontFamily: "inherit",
    },
    dangerBtn: {
      padding: "8px 10px",
      borderRadius: 10,
      background: "transparent",
      color: theme.errorText,
      border: `1px solid ${theme.errorBorder}`,
      fontWeight: 700, fontSize: 12, cursor: "pointer",
      fontFamily: "inherit",
    },
  };

  return (
    <div style={css.card} data-testid={`client-card-${u.mobile_number || u.id}`}>
      <div style={css.top}>
        <div style={css.avatar}>{initial}</div>
        <div style={css.nameCol}>
          <div style={css.name}>{u.full_name || "—"}</div>
          <div style={css.mobile}>{u.mobile_number || "—"}</div>
        </div>
        <span style={css.marketingBadge}>{T.marketing_badge}</span>
      </div>

      {u.email && (
        <div style={css.email} title={u.email}>
          <span style={{ opacity: 0.6 }}>@</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</span>
        </div>
      )}

      {/* Quick marketing/operations actions */}
      <div style={css.quickRow}>
        <a
          href={whatsappHref || undefined}
          target="_blank" rel="noopener noreferrer"
          onClick={(e) => { if (!whatsappHref) e.preventDefault(); }}
          style={{
            ...css.quickBtn("#16a34a", "rgba(22,163,74,0.10)", "rgba(22,163,74,0.28)"),
            opacity: whatsappHref ? 1 : 0.4, pointerEvents: whatsappHref ? "auto" : "none",
          }}
          data-testid={`client-whatsapp-${u.id}`}
        >
          <MessageCircle size={13} strokeWidth={2.2} />
          {T.whatsapp}
        </a>
        <a
          href={callHref || undefined}
          onClick={(e) => { if (!callHref) e.preventDefault(); }}
          style={{
            ...css.quickBtn("#3B82F6", "rgba(59,130,246,0.10)", "rgba(59,130,246,0.28)"),
            opacity: callHref ? 1 : 0.4, pointerEvents: callHref ? "auto" : "none",
          }}
          data-testid={`client-call-${u.id}`}
        >
          <Phone size={13} strokeWidth={2.2} />
          {T.call}
        </a>
        <button
          type="button"
          onClick={() => onAction("view_apps")}
          style={css.quickBtn("#D4AF37", "rgba(212,175,55,0.10)", "rgba(212,175,55,0.28)")}
          data-testid={`client-view-apps-${u.id}`}
        >
          <ClipboardList size={13} strokeWidth={2.2} />
          {T.view_applications}
        </button>
      </div>

      <div style={css.footer}>
        <button
          type="button"
          onClick={() => onAction("edit")}
          style={css.ghostBtn}
          data-testid={`client-edit-${u.id}`}
        >
          {T.edit}
        </button>
        <button
          type="button"
          onClick={() => onAction("delete")}
          style={css.dangerBtn}
          data-testid={`client-delete-${u.id}`}
          title={T.delete}
          aria-label={T.delete}
        >
          {T.delete}
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────── Modal ────────────────────────── */
function UserModal({ modal, roles, theme, lang, T, onClose, onDone }) {
  const { kind, user: target } = modal;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState(() => {
    if (kind === "create") {
      return {
        user_type: modal.defaultType || "internal",
        username: "",
        password: "",
        full_name: "",
        mobile_number: "",
        email: "",
        role_id: roles.find((r) => !r.is_root && r.code === "employee")?.id || "",
      };
    }
    if (kind === "edit") {
      return {
        username: target.username || "",
        full_name: target.full_name || "",
        mobile_number: target.mobile_number || "",
        email: target.email || "",
        role_id: target.role_id || "",
      };
    }
    if (kind === "password") {
      return { new_password: "", confirm_password: "" };
    }
    return {};
  });

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      if (kind === "create") {
        await api.post("/users", form);
      } else if (kind === "edit") {
        await api.patch(`/users/${target.id}`, form);
      } else if (kind === "password") {
        if (form.new_password !== form.confirm_password) {
          setErr(T.err_password_mismatch);
          setBusy(false);
          return;
        }
        await api.post(`/users/${target.id}/change-password`, form);
      } else if (kind === "toggle") {
        await api.post(`/users/${target.id}/toggle-active`);
      } else if (kind === "delete") {
        await api.delete(`/users/${target.id}`);
      } else if (kind === "switch") {
        const { data } = await api.post(`/users/${target.id}/switch`);
        localStorage.setItem("vta_token", data.access_token);
        onDone(true);
        return;
      }
      onDone(false);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const css = makeModalStyles(theme);
  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const titleMap = {
    create: T.modal_create,
    edit: T.modal_edit,
    password: T.modal_password,
    toggle: target?.is_active ? T.modal_deactivate : T.modal_activate,
    delete: T.modal_delete,
    switch: T.modal_switch,
  };

  const confirmKinds = ["toggle", "delete", "switch"];
  const isConfirm = confirmKinds.includes(kind);

  return (
    <div style={css.backdrop} onClick={onClose} data-testid="user-modal-backdrop">
      <div style={css.modal} onClick={(e) => e.stopPropagation()} data-testid={`user-modal-${kind}`}>
        <div style={css.header}>
          <div style={css.title}>{titleMap[kind]}</div>
          <button onClick={onClose} style={css.close} data-testid="user-modal-close">✕</button>
        </div>

        {kind === "create" && (
          <div style={css.body}>
            <Field label={T.user_type}>
              <select
                value={form.user_type}
                onChange={onChange("user_type")}
                style={css.input}
                data-testid="modal-user-type"
              >
                <option value="internal">{T.internal}</option>
                <option value="client">{T.client}</option>
              </select>
            </Field>
            {form.user_type === "client" && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 8,
                padding: "10px 12px", marginBottom: 12,
                borderRadius: 10,
                background: "rgba(22,163,74,0.10)",
                border: "1px solid rgba(22,163,74,0.28)",
                color: theme.textSoft, fontSize: 12, lineHeight: 1.6,
              }} data-testid="modal-marketing-hint">
                <ShieldCheck size={16} strokeWidth={2} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{T.marketing_only_hint}</span>
              </div>
            )}
            {form.user_type === "internal" && (
              <>
                <Field label={T.username}>
                  <SecretInput value={form.username} onChange={onChange("username")} theme={theme} type="text" testid="modal-username" autoComplete="off" placeholder="مثل: ahmed" />
                </Field>
                <Field label={T.new_password}>
                  <SecretInput value={form.password} onChange={onChange("password")} theme={theme} type="password" testid="modal-password" autoComplete="new-password" placeholder="حرف، رقم، رمز — حر بدون شروط" />
                </Field>
              </>
            )}
            <Field label={T.full_name}>
              <input style={css.input} value={form.full_name} onChange={onChange("full_name")} data-testid="modal-full-name" />
            </Field>
            <Field label={form.user_type === "client" ? `${T.mobile} *` : T.mobile}>
              <input style={css.input} value={form.mobile_number} onChange={onChange("mobile_number")} data-testid="modal-mobile" placeholder={form.user_type === "client" ? "05xxxxxxxx" : ""} />
            </Field>
            <Field label={T.email}>
              <input style={css.input} value={form.email} onChange={onChange("email")} data-testid="modal-email" autoComplete="off" placeholder="اختياري — أي صيغة" />
            </Field>
            {form.user_type === "internal" && (
              <Field label={T.role}>
                <select style={css.input} value={form.role_id || ""} onChange={onChange("role_id")} data-testid="modal-role">
                  <option value="">—</option>
                  {roles.filter((r) => !r.is_root).map((r) => (
                    <option key={r.id} value={r.id}>{lang === "en" ? r.name_en || r.name_ar : r.name_ar}</option>
                  ))}
                </select>
              </Field>
            )}
          </div>
        )}

        {kind === "edit" && (
          <div style={css.body}>
            {target.user_type === "client" && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 8,
                padding: "10px 12px", marginBottom: 12,
                borderRadius: 10,
                background: "rgba(22,163,74,0.10)",
                border: "1px solid rgba(22,163,74,0.28)",
                color: theme.textSoft, fontSize: 12, lineHeight: 1.6,
              }} data-testid="modal-edit-marketing-hint">
                <ShieldCheck size={16} strokeWidth={2} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{T.marketing_only_hint}</span>
              </div>
            )}
            {target.user_type === "internal" && (
              <Field label={T.username}>
                <SecretInput value={form.username} onChange={onChange("username")} theme={theme} type="text" testid="modal-edit-username" autoComplete="off" />
              </Field>
            )}
            <Field label={T.full_name}>
              <input style={css.input} value={form.full_name} onChange={onChange("full_name")} data-testid="modal-edit-full-name" />
            </Field>
            <Field label={target.user_type === "client" ? `${T.mobile} *` : T.mobile}>
              <input style={css.input} value={form.mobile_number} onChange={onChange("mobile_number")} data-testid="modal-edit-mobile" />
            </Field>
            <Field label={T.email}>
              <input style={css.input} value={form.email} onChange={onChange("email")} data-testid="modal-edit-email" autoComplete="off" placeholder="اختياري — أي صيغة" />
            </Field>
            {target.user_type === "internal" && !target.role?.is_root && (
              <Field label={T.role}>
                <select style={css.input} value={form.role_id || ""} onChange={onChange("role_id")} data-testid="modal-edit-role">
                  <option value="">—</option>
                  {roles.filter((r) => !r.is_root).map((r) => (
                    <option key={r.id} value={r.id}>{lang === "en" ? r.name_en || r.name_ar : r.name_ar}</option>
                  ))}
                </select>
              </Field>
            )}
          </div>
        )}

        {kind === "password" && (
          <div style={css.body}>
            <div style={css.hint}>{T.password_hint(target.full_name)}</div>
            <Field label={T.new_password}>
              <SecretInput value={form.new_password} onChange={onChange("new_password")} theme={theme} type="password" testid="modal-new-password" autoComplete="new-password" placeholder="حر — بدون شروط" />
            </Field>
            <Field label={T.confirm_password}>
              <SecretInput value={form.confirm_password} onChange={onChange("confirm_password")} theme={theme} type="password" testid="modal-confirm-password" autoComplete="new-password" />
            </Field>
          </div>
        )}

        {isConfirm && (
          <div style={css.body}>
            <div style={{ color: theme.text, fontSize: "14px", lineHeight: 1.7 }}>
              {kind === "toggle" && (target.is_active ? T.confirm_deactivate(target.full_name) : T.confirm_activate(target.full_name))}
              {kind === "delete" && (
                <span style={{ color: theme.errorText }}>{T.confirm_delete(target.full_name)}</span>
              )}
              {kind === "switch" && T.confirm_switch(target.full_name)}
            </div>
          </div>
        )}

        {err && <div style={css.error} data-testid="user-modal-error">{err}</div>}

        <div style={css.footer}>
          <button onClick={onClose} style={css.btnGhost} data-testid="user-modal-cancel" disabled={busy}>
            {T.cancel}
          </button>
          <button
            onClick={submit}
            disabled={busy}
            style={kind === "delete" ? css.btnDanger : css.btnPrimary}
            data-testid="user-modal-submit"
          >
            {busy ? "…" : (kind === "delete" ? T.confirm_delete_btn : T.confirm)}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: "12px" }}>
      <div style={{ fontSize: "11px", marginBottom: "4px", opacity: 0.7, letterSpacing: "0.4px" }}>{label}</div>
      {children}
    </label>
  );
}

/* ─────────── Reusable: input + show/hide eye + copy button (free-form) ─────────── */
function SecretInput({ value, onChange, theme, type = "text", testid, autoComplete = "off", placeholder }) {
  const [show, setShow] = useState(type !== "password");
  const [copied, setCopied] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword && !show ? "password" : "text";

  const onCopy = async () => {
    try {
      if (!value) return;
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // Fallback: select + execCommand (older browsers / non-secure contexts)
      try {
        const ta = document.createElement("textarea");
        ta.value = String(value || ""); document.body.appendChild(ta);
        ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
        setCopied(true); setTimeout(() => setCopied(false), 1200);
      } catch { /* ignore */ }
    }
  };

  const btn = {
    width: 36, height: 36, borderRadius: 8, cursor: "pointer",
    background: "transparent", color: theme.text,
    border: `1px solid ${theme.border}`,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  };

  return (
    <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
      <input
        type={inputType}
        value={value || ""}
        onChange={onChange}
        data-testid={testid}
        autoComplete={autoComplete}
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        inputMode="text"
        dir="ltr"
        placeholder={placeholder}
        style={{
          flex: 1, padding: "10px 12px", borderRadius: 8,
          background: theme.bg, color: theme.text,
          border: `1px solid ${theme.border}`, fontSize: 14,
          outline: "none", direction: "ltr", textAlign: "left",
        }}
      />
      {isPassword && (
        <button type="button" onClick={() => setShow((s) => !s)}
          title={show ? "إخفاء" : "إظهار"}
          aria-label={show ? "إخفاء" : "إظهار"}
          data-testid={`${testid}-eye`}
          style={btn}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      )}
      <button type="button" onClick={onCopy} disabled={!value}
        title="نسخ"
        aria-label="نسخ"
        data-testid={`${testid}-copy`}
        style={{ ...btn, opacity: value ? 1 : 0.4, cursor: value ? "pointer" : "not-allowed" }}
      >
        {copied ? <Check size={16} color="#16a34a" /> : <Copy size={16} />}
      </button>
    </div>
  );
}

/* ────────────────────────── Styles ────────────────────────── */
const makeStyles = (theme) => ({
  page: {
    minHeight: "100vh",
    background: theme.bg,
    color: theme.text,
    fontFamily: "'Tajawal','Segoe UI', sans-serif",
    padding: "16px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px",
    padding: "12px 0",
    borderBottom: `1px solid ${theme.borderSoft}`,
  },
  backBtn: {
    background: "transparent",
    border: `1px solid ${theme.border}`,
    color: theme.text,
    borderRadius: "8px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "13px",
  },
  title: { fontSize: "18px", fontWeight: 800, color: theme.gold, minWidth: "140px", textAlign: "center" },
  titleWrap: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  primaryBtn: {
    background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none",
    padding: "9px 16px",
    borderRadius: "8px",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: "13px",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    alignItems: "center",
    marginBottom: "16px",
  },
  tabRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "10px",
    marginBottom: "14px",
  },
  tabBtn: (active, color) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 14px",
    borderRadius: "12px",
    border: `1.5px solid ${active ? color : theme.borderSoft}`,
    background: active
      ? (theme.name === "dark" ? `${color}22` : `${color}18`)
      : theme.surfaceCard,
    color: theme.text,
    cursor: "pointer",
    textAlign: "start",
    fontFamily: "inherit",
    transition: "border-color 150ms, background 150ms, transform 150ms",
    boxShadow: active ? `0 0 0 1px ${color}55` : "none",
  }),
  tabIconWrap: (active, color) => ({
    width: 40,
    height: 40,
    borderRadius: "10px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: active ? color : (theme.name === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"),
    border: `1px solid ${active ? color : theme.borderSoft}`,
    flexShrink: 0,
  }),
  tabTexts: { display: "flex", flexDirection: "column", flex: 1, minWidth: 0 },
  tabLabel: { fontWeight: 800, fontSize: "14px", color: theme.text },
  tabHint: { fontSize: "11px", color: theme.textMuted, marginTop: "2px" },
  tabCount: (active, color) => ({
    minWidth: 32,
    padding: "3px 10px",
    borderRadius: "999px",
    background: active ? color : (theme.name === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
    color: active ? "#fff" : theme.textMuted,
    fontWeight: 900,
    fontSize: "12px",
    textAlign: "center",
    fontFamily: "monospace",
    flexShrink: 0,
  }),
  currentBucket: {
    fontSize: "12px",
    color: theme.textMuted,
    fontWeight: 700,
    padding: "6px 12px",
    background: theme.name === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
    borderRadius: "999px",
  },
  search: {
    flex: "1 1 200px",
    background: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    color: theme.text,
    padding: "10px 12px",
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily: "inherit",
  },
  filterGroup: { display: "flex", gap: "6px", flexWrap: "wrap" },
  filterBtn: (active) => ({
    padding: "8px 14px",
    borderRadius: "8px",
    border: `1px solid ${active ? theme.gold : theme.border}`,
    background: active
      ? (theme.name === "dark" ? "rgba(212,175,55,0.15)" : "rgba(184,144,42,0.15)")
      : "transparent",
    color: active ? theme.gold : theme.text,
    fontWeight: 700,
    cursor: "pointer",
    fontSize: "12px",
  }),
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "12px",
  },
  loading: { padding: "40px", textAlign: "center", color: theme.textMuted },
  empty: { padding: "40px", textAlign: "center", color: theme.textMuted },
  error: {
    padding: "12px",
    background: theme.errorBg,
    border: `1px solid ${theme.errorBorder}`,
    color: theme.errorText,
    borderRadius: "8px",
    marginBottom: "12px",
    fontSize: "13px",
  },
});

const makeModalStyles = (theme) => ({
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(4px)",
    zIndex: 9000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    animation: "fade-in 150ms ease",
  },
  modal: {
    background: theme.surface,
    border: `1px solid ${theme.borderAccent}`,
    borderRadius: "14px",
    width: "100%",
    maxWidth: "440px",
    maxHeight: "92vh",
    overflowY: "auto",
    boxShadow: theme.cardShadow,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 18px",
    borderBottom: `1px solid ${theme.borderSoft}`,
  },
  title: { fontWeight: 800, color: theme.gold, fontSize: "15px" },
  close: {
    background: "transparent",
    border: "none",
    color: theme.textMuted,
    fontSize: "18px",
    cursor: "pointer",
    padding: "4px 8px",
  },
  body: { padding: "16px 18px" },
  input: {
    width: "100%",
    background: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    color: theme.text,
    padding: "9px 11px",
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  hint: {
    fontSize: "12px",
    color: theme.textMuted,
    marginBottom: "12px",
    padding: "8px 10px",
    background: theme.name === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
    borderRadius: "6px",
  },
  footer: {
    display: "flex",
    gap: "8px",
    padding: "12px 18px 16px",
    justifyContent: "flex-end",
    borderTop: `1px solid ${theme.borderSoft}`,
  },
  btnGhost: {
    background: "transparent",
    border: `1px solid ${theme.border}`,
    color: theme.text,
    padding: "9px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "13px",
  },
  btnPrimary: {
    background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none",
    padding: "9px 18px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 800,
    fontSize: "13px",
  },
  btnDanger: {
    background: "#dc2626",
    color: "#fff",
    border: "none",
    padding: "9px 18px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 800,
    fontSize: "13px",
  },
  error: {
    margin: "0 18px",
    padding: "10px",
    background: theme.errorBg,
    border: `1px solid ${theme.errorBorder}`,
    color: theme.errorText,
    borderRadius: "8px",
    fontSize: "12px",
  },
});
